// @ts-check
// src/lib/session-expiry.js
// 2026-08-18 — единая точка «сервер отклонил нашу сессию».
//
// Зачем: до этого протухшая при открытой вкладке сессия была НЕВИДИМА. Токен молча
// стирался в apiGet/apiSave (401 после повторов), сохранения оседали в pendingSaves,
// WS закрывался кодом 4001 и переставал переподключаться — и всё это только в консоли.
// Человек продолжал работать во вкладке, которая уже ничего не пишет на сервер.
// sessionExpiredFlag тут не помогает: его читают ровно в одном месте — загрузчике
// App.jsx (useEffect[user]), то есть на старте страницы, а не по ходу работы.
//
// Контракт: тот, кто увидел 401, НЕ показывает модалку сам, а зовёт
// confirmSessionExpired — та делает контрольный запрос к /api/session тем же токеном.
// Модалка встаёт только если сервер отказал ДВАЖДЫ подряд. Это правило проекта, а не
// перестраховка: v31.66 чинил ровно то, что одиночный 401 (транзиентный: рестарт,
// сетевая икота, гонка) убивал живую сессию. Блокирующий экран по одиночному отказу
// был бы той же ошибкой, только громче.

import { API_BASE } from './endpoints.js';
import { getSessionToken, getRawSession } from '../utils/session.js';

export const SESSION_EXPIRED_EVENT = 'blitz_session_expired';
// Приложение само увело человека на экран входа — модалка больше не нужна (и не должна
// висеть поверх формы входа).
export const SESSION_EXPIRED_CLEARED_EVENT = 'blitz_session_expired_cleared';
// 2026-08-31 (Phase 1 session TTL, П3) — локальное ПОДОЗРЕНИЕ: срок истёк по часам этой
// вкладки, но сервер недостижим (офлайн), так что подтвердить/опровергнуть нечем. НЕ то
// же самое, что SESSION_EXPIRED_EVENT (подтверждённая двумя 401 смерть) — снимаемое,
// не встаёт на терминальный alreadyNotified. См. raiseLocalSuspicion/clearLocalSuspicion.
export const SESSION_SUSPECT_EVENT = 'blitz_session_suspect';
export const SESSION_SUSPECT_CLEARED_EVENT = 'blitz_session_suspect_cleared';

/** Сколько ждать ответа на контрольный запрос. */
const PROBE_TIMEOUT_MS = 5000;
/** Пауза между двумя контрольными запросами. */
const PROBE_RETRY_DELAY_MS = 400;
// 2026-08-31 (Phase 1 session TTL, П2) — тот же 23h запас, которым раньше жил только
// App.jsx getSession() (v31.66: TTL 7d → 23h, чтобы НЕ превышать backend SESSION_TTL 24h).
// Один дом для порога — getSession() теперь тоже зовёт localSessionExpired ниже.
const LOCAL_SESSION_TTL_MS = 23 * 60 * 60 * 1000;

// Замок на одно событие за жизнь страницы: загрузчик тянет 15 таблиц одним Promise.all,
// у мёртвой сессии это 15 отказов подряд — модалке хватит первого.
let alreadyNotified = false;
// Поколение сессии. Растёт на каждом входе и на каждом «приложение само разлогинило».
// Нужно потому, что проверка асинхронная: пока она летит, человек может успеть войти
// заново — и вердикт по СТАРОМУ, уже мёртвому токену встал бы модалкой поверх свежей
// рабочей сессии.
let sessionEpoch = 0;
// Поколение, для которого прямо сейчас летит проверка (-1 = не летит). Параллельные
// отказы одного поколения не плодят контрольные запросы, но новое поколение не ждёт
// хвоста старого — сравнение именно с текущим поколением, а не «летит хоть что-то».
// Сбрасывать это поле при смене поколения НЕ нужно и нельзя: сброс сделал бы сравнение
// с поколением декоративным, и подмена его на «!== -1» прошла бы мимо тестов.
let confirmingEpoch = -1;
// 2026-08-31 (Phase 1 session TTL, П3) — локальное подозрение этой вкладки. Отдельно от
// alreadyNotified специально: подтверждённая смерть (два 401) не должна теряться из-за
// более позднего «подозрение снято», и наоборот — подозрение не смеет глушить настоящую
// проверку по 401. Read-only UI-состояние: не пишет в localStorage, не трогает токен.
let localSuspicion = false;

/**
 * Сообщить приложению, что сессия мертва. Вызывать напрямую только когда вердикт уже
 * подтверждён (см. confirmSessionExpired).
 *
 * @param {string} [reason] Откуда пришёл вердикт — для консоли и разбора инцидентов.
 * @returns {boolean} true, если событие ушло (false — замок уже стоял / нет window).
 */
export function notifySessionExpired(reason = 'unknown') {
  if (typeof window === 'undefined') return false;
  if (alreadyNotified) return false;
  alreadyNotified = true;
  console.warn(`🔒 SESSION_EXPIRED (${reason}) — showing refresh prompt`);
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { reason } }));
  return true;
}

/**
 * Снять замок. Зовётся после успешного входа: в той же вкладке может протухнуть уже
 * следующая сессия, и о ней тоже нужно сказать. Вход (в этой вкладке или — через П4 —
 * замеченный по смене токена вход в соседней) снимает и локальное подозрение: сомневаться
 * больше не в чем.
 *
 * 2026-09-01 (фаза3 П1, свод критики) — раньше это событие слала только
 * suppressSessionExpiredNotice («приложение само разлогинило»). Реальный вход (LoginScreen
 * ИЛИ форма входа внутри модалки, App.jsx completeLogin) звал только эту функцию без
 * события — Gate слушает именно SESSION_EXPIRED_CLEARED_EVENT, поэтому модалка технически
 * не закрывалась после успешного входа. Единственный прод-вызыватель этой функции —
 * App.jsx completeLogin (~:2798), так что дописать событие сюда безопасно. Двойное
 * событие (эта функция + suppress могут сработать одно за другим в разных сценариях) не
 * страшно — Gate-слушатель (onCleared = setExpired(false)) идемпотентен к повторам.
 * @returns {void}
 */
export function resetSessionExpiredNotice() {
  alreadyNotified = false;
  sessionEpoch++;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_CLEARED_EVENT));
  }
  clearLocalSuspicion();
}

/**
 * Приложение само разлогинило человека (загрузочная проверка сессии подтвердила смерть,
 * показан экран входа). Модалка тут лишняя: замок взводим, уже показанную — убираем.
 * Заодно снимаем подозрение — иначе оно всплыло бы модалкой поверх формы входа.
 * @returns {void}
 */
export function suppressSessionExpiredNotice() {
  alreadyNotified = true;
  sessionEpoch++;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_CLEARED_EVENT));
  }
  clearLocalSuspicion();
}

/**
 * Поднять локальное подозрение: локальный срок истёк, а сервер недостижим (офлайн) —
 * подтвердить или снять вердикт нечем. Идемпотентно — повторный вызов, пока подозрение
 * уже стоит, ничего не делает и не шлёт событие повторно.
 *
 * @param {string} [reason]
 * @returns {boolean} true, если событие ушло.
 */
export function raiseLocalSuspicion(reason = 'unknown') {
  if (typeof window === 'undefined') return false;
  if (localSuspicion) return false;
  localSuspicion = true;
  window.dispatchEvent(new CustomEvent(SESSION_SUSPECT_EVENT, { detail: { reason } }));
  return true;
}

/**
 * Снять локальное подозрение. Зовётся при любом сигнале «сервер жив» (probe onAlive,
 * дешёвая отметка успешного apiGet), при входе (resetSessionExpiredNotice /
 * suppressSessionExpiredNotice — оба зовут это сами) и при смене токена в другой вкладке.
 * @returns {boolean} true, если подозрение реально стояло и событие ушло.
 */
export function clearLocalSuspicion() {
  if (!localSuspicion) return false;
  localSuspicion = false;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SESSION_SUSPECT_CLEARED_EVENT));
  }
  return true;
}

/**
 * Стоит ли сейчас локальное подозрение (для read-only проверок, напр. в тестах).
 * @returns {boolean}
 */
export function isLocalSuspicionActive() {
  return localSuspicion;
}

/**
 * Вердикт уже вынесен — сессия признана мёртвой в этой жизни страницы.
 * Нужен путям, которые сами решают, что делать с недоставленными данными: повторять
 * бессмысленно, надо спасать в durable store.
 *
 * @returns {boolean}
 */
export function isSessionKnownDead() {
  return alreadyNotified;
}

/**
 * Контрольный запрос: сервер действительно не знает этот токен?
 * Приговор — только два 401 подряд. Сеть/таймаут/429/5xx = «не знаем» → сессию не хороним.
 *
 * 2026-08-31 (Phase 1 session TTL, П2): раньше первая же сетевая ошибка обрывала цикл
 * немедленно (return false после одной попытки) — «офлайн» и «сервер ответил не-401»
 * были неразличимы для вызывающего. Теперь сетевая ошибка НЕ обрывает retry — попытка
 * просто засчитывается как network-попытка и цикл идёт до конца (те же 2 попытки, та же
 * пауза между ними), а различаются исходы: `dead` (обе попытки — РЕАЛЬНЫЕ 401, двух-401
 * принцип не ослаблен: сетевая попытка в счёт двух 401 не идёт), `reallyAlive` (сервер
 * ОТВЕТИЛ чем-то не-401 — 200/403/429/5xx: конкретный сигнал «сервер жив», не путать с
 * unreachable) и `unreachable` (обе попытки упали сетью — офлайн, а не протухшая сессия).
 * Смешанный случай (одна сеть, одна попытка получила 401) — ни то, ни другое, ни третье:
 * консервативно «не знаем» (все три флага false; см. фикс-раунд FR2 — не основание
 * продлевать локальный срок по одному неподтверждённому 401).
 *
 * @param {string} token
 * @returns {Promise<{dead: boolean, reallyAlive: boolean, unreachable: boolean}>}
 */
async function probeSessionDead(token) {
  const outcomes = [];
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt > 0) await new Promise(r => setTimeout(r, PROBE_RETRY_DELAY_MS));
    let res;
    try {
      res = await fetch(`${API_BASE}/session`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Client-Time': String(Date.now()),
        },
        signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
      });
    } catch {
      outcomes.push('network');
      continue;
    }
    // сервер жив, отвечает о другом — конкретный не-401 ответ, второй попытки не нужно
    if (res.status !== 401) return { dead: false, reallyAlive: true, unreachable: false };
    outcomes.push(401);
  }
  const bothDead = outcomes[0] === 401 && outcomes[1] === 401;
  const bothUnreachable = outcomes[0] === 'network' && outcomes[1] === 'network';
  return { dead: bothDead, reallyAlive: false, unreachable: bothUnreachable };
}

/**
 * Подтвердить смерть сессии и, если подтвердилась, показать модалку.
 *
 * @param {{token: string|null|undefined, reason: string, onAlive?: () => void, onReallyAlive?: () => void, onUnreachable?: () => void}} opts
 *   token         — токен, которым получили отказ (взять ДО того, как его сотрут);
 *   reason        — откуда пришёл отказ;
 *   onAlive       — что сделать, если сессия оказалась жива (например, вернуть стёртый
 *                   токен, api-client restoreKilledSession). Зовётся при ЛЮБОМ «не
 *                   мертва» исходе — включая недостижимый сервер: НЕ ИЗМЕНЕНО фикс-раундом
 *                   (жюри B / cursor#1) — 6 старых вызывающих полагаются на этот контракт
 *                   ровно как раньше (см. тест «сеть упала → молчим ... onAlive called»).
 *   onReallyAlive — 2026-08-31 (фикс-раунд FR2/FR3): ТОЛЬКО когда сервер дал КОНКРЕТНЫЙ
 *                   не-401 ответ (200/403/429/5xx) — не путать с onAlive: не зовётся на
 *                   unreachable и не зовётся на смешанный сеть+401 исход. Раздельный
 *                   колбэк специально ради checkLocalSessionExpiry (продление loginTime
 *                   — только по-настоящему живому серверу, не по «не смогли достучаться»).
 *   onUnreachable — 2026-08-31 (П2/П3): доп. сигнал ТОЛЬКО когда обе попытки упали
 *                   сетью — офлайн-кейс (Sophia). Не заменяет onAlive, идёт вдобавок.
 * @returns {Promise<boolean>} true — сессия мертва, модалка показана.
 */
export async function confirmSessionExpired({ token, reason, onAlive, onReallyAlive, onUnreachable }) {
  if (!token) return false;
  if (alreadyNotified) return false;
  const epoch = sessionEpoch;
  if (confirmingEpoch === epoch) return false;  // проверка этого поколения уже летит
  confirmingEpoch = epoch;
  try {
    const { dead, reallyAlive, unreachable } = await probeSessionDead(token);
    // Пока спрашивали, человек мог выйти и войти заново. Вердикт по токену прошлого
    // поколения к новой сессии отношения не имеет — ни хоронить, ни воскрешать.
    if (epoch !== sessionEpoch) {
      console.warn(`⏭️ SESSION_VERDICT_STALE (${reason}) — сессия успела смениться, вердикт отброшен`);
      return false;
    }
    if (alreadyNotified) return false;
    if (dead) return notifySessionExpired(reason);
    console.warn(`✅ SESSION_FALSE_ALARM (${reason}) — сервер принял тот же токен, сессия жива`);
    try {
      if (onAlive) onAlive();
    } catch (err) {
      // Возврат сессии не должен ронять вызывающего: его никто не ждёт (проверка идёт
      // фоном), и необработанный reject ушёл бы в Sentry шумом.
      console.error('SESSION_RESTORE_FAILED', err);
    }
    if (reallyAlive) {
      try {
        if (onReallyAlive) onReallyAlive();
      } catch (err) {
        console.error('SESSION_REALLY_ALIVE_HANDLER_FAILED', err);
      }
    }
    if (unreachable) {
      try {
        if (onUnreachable) onUnreachable();
      } catch (err) {
        console.error('SESSION_UNREACHABLE_HANDLER_FAILED', err);
      }
    }
    return false;
  } finally {
    if (confirmingEpoch === epoch) confirmingEpoch = -1;
  }
}

/**
 * Локальная оценка срока — чистая функция, без сети и без localStorage-чтения. `null`/без
 * loginTime = «не знаем» = не подозреваем (false), не терминальный вердикт.
 * Порог — те же 23h, которыми раньше жил только App.jsx getSession() (см. LOCAL_SESSION_TTL_MS).
 *
 * @param {{loginTime?: number}|null|undefined} session
 * @param {number} now
 * @returns {boolean}
 */
export function localSessionExpired(session, now) {
  if (!session || typeof session.loginTime !== 'number') return false;
  return now - session.loginTime > LOCAL_SESSION_TTL_MS;
}

/**
 * Продлить локальный срок: сервер ПОДТВЕРДИЛ конкретным не-401 ответом, что сессия жива —
 * значит расхождение с локальными часами (спешащие часы, CMOS, кривая дата виртуалки —
 * критика Opus B2), а не протухшая сессия. Boot-путь (App.jsx Step 1.5 probe → reconcile
 * → saveSession) уже делает ровно это; фикс-раунд FR2 убирает асимметрию с runtime-путём
 * — без него create-gate (FR1) держал бы сотрудника со спешащими часами заблокированным
 * НАВСЕГДА, хотя сессия рабочая. Raw write: меняем только loginTime, остальные поля
 * (токен, cpDesign и т.д.) — как есть.
 *
 * @returns {void}
 */
function extendLocalLoginTime() {
  try {
    const session = getRawSession();
    if (!session) return;
    session.loginTime = Date.now();
    localStorage.setItem('blitz_session', JSON.stringify(session));
  } catch {
    // localStorage недоступен/полон — не страшно: не продлили сейчас, продлим на
    // следующий подтверждённо-живой тик. Никогда не роняем оркестратор из-за этого.
  }
}

/**
 * Оркестратор локальной проверки срока сессии — единая точка входа для всех триггеров
 * (visibilitychange/focus/interval в App.jsx, create-submit/bulk-duplicate/Revert гейт на
 * Dashboard/CustomerPayments — см. blockIfSessionExpired).
 *
 * Читает `blitz_session` СВЕЖЕЙ из localStorage при КАЖДОМ вызове — никаких замыканий,
 * это мультивкладочная гигиена (соседняя вкладка могла обновить сессию между вызовами).
 *
 * Не истекла -> снимает подозрение, если стояло, и выходит без единого сетевого запроса
 * (значит и триггер по interval «бесплатен», пока сессия жива). Истекла -> СНАЧАЛА
 * спрашивает сервер (confirmSessionExpired — двух-401 принцип нетронут):
 *  - сервер подтвердил живость конкретным ответом (onReallyAlive) -> продлить loginTime
 *    (FR2, анти-локаут) + снять подозрение;
 *  - сервер недостижим (onUnreachable, обе попытки сетью) -> поднять подозрение, loginTime
 *    НЕ трогать.
 * Раздельные колбэки (а не общий onAlive, который зовётся на оба исхода ради обратной
 * совместимости с другими вызывающими confirmSessionExpired) — это же убирает флап
 * clear→raise на каждом офлайн-тике (FR3): пока подозрение стоит, onReallyAlive на
 * unreachable-тиках не зовётся вовсе, а raiseLocalSuspicion идемпотентна.
 *
 * @param {string} [reason]
 * @returns {Promise<void>}
 */
export async function checkLocalSessionExpiry(reason = 'unknown') {
  let session = null;
  try {
    session = JSON.parse(localStorage.getItem('blitz_session') || 'null');
  } catch {
    session = null;
  }
  if (!localSessionExpired(session, Date.now())) {
    clearLocalSuspicion();
    return;
  }
  await confirmSessionExpired({
    token: getSessionToken(),
    reason,
    onReallyAlive: () => {
      extendLocalLoginTime();
      clearLocalSuspicion();
    },
    onUnreachable: () => raiseLocalSuspicion(reason),
  });
}

/**
 * Общий гейт для точек СОЗДАНИЯ/ВОССТАНОВЛЕНИЯ (create-submit, bulk-duplicate, Revert —
 * фикс-раунд FR1): истёкшая локальная сессия не должна плодить новые оптимистичные
 * записи. Потери данных тут нет (после фазы 0 apiSave кладёт недоставленное в очередь) —
 * цель именно консистентность «истёкшая сессия не создаёт новое», не защита от потери.
 *
 * @param {string} reason — передаётся в checkLocalSessionExpiry (probe/suspicion reason).
 * @param {(message: string, level?: string) => void} [notify] — тост-функция ВЫЗЫВАЮЩЕЙ
 *   страницы (`addToast` в Dashboard, `toast` в CustomerPayments — имена страниц
 *   расходятся, поэтому параметр, а не прямой импорт из конкретной страницы).
 * @returns {boolean} true — заблокировано, вызывающий обязан прервать создание (return).
 */
export function blockIfSessionExpired(reason, notify) {
  if (!localSessionExpired(getRawSession(), Date.now())) return false;
  checkLocalSessionExpiry(reason);
  if (notify) notify('Session expired — please sign in again', 'error');
  return true;
}
